import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

// API base selection for website editor routes:
// - If env provided, use it.
// - Otherwise, on production frontend (explanatum.co.uk), use relative paths to leverage Vercel rewrites.
const API_BASE = (() => {
  const envBase = (process.env.REACT_APP_API_URL || (process.env as any).NEXT_PUBLIC_API_BASE || '').replace(/\/$/, '');
  if (envBase) return envBase;
  if (typeof window !== 'undefined') {
    const h = window.location.hostname || '';
    if (h.endsWith('explanatum.co.uk')) return '';
  }
  return '';
})();

// Minimal types to keep this file self-contained
interface V3Section {
  id: string;
  kind?: string;
  heading?: string;
  body?: string;
  html?: string;
  [key: string]: any;
}

interface V3Metadata { [key: string]: any }

interface AssembleResponse { html?: string }
interface SaveWebsiteResponse { website: { id: string } }
interface DeployResponse { url?: string; deploymentId?: string }
interface GetWebsiteResponse { website: { id: string; html: string; website_data?: { metadata?: V3Metadata; sections?: V3Section[] } } }

// Small helper copied locally (timeout + basic error surfacing)
async function requestJson(url: string, options: RequestInit, defaultError: string, timeoutMs = 20000): Promise<any> {
  const controller = new AbortController();
  const to = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const resp = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(to);
    const ct = resp.headers.get('content-type') || '';
    if (!ct.toLowerCase().includes('application/json')) {
      const t = await resp.text();
      if (!resp.ok) throw new Error(`HTTP ${resp.status}: ${t.slice(0, 300) || defaultError}`);
      throw new Error(`Unexpected non-JSON response: ${t.slice(0, 300) || defaultError}`);
    }
    const data = await resp.json();
    if (!resp.ok) {
      throw new Error((data && (data.error || data.message)) || defaultError);
    }
    return data;
  } catch (e: any) {
    clearTimeout(to);
    throw new Error(e?.message || defaultError);
  }
}

const Editor: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const state = (location.state || {}) as { metadata?: V3Metadata; sections?: V3Section[]; html?: string; websiteId?: string };

  const [metadata, setMetadata] = useState<V3Metadata | null>(state.metadata || null);
  const [sections, setSections] = useState<V3Section[]>(Array.isArray(state.sections) ? state.sections : []);
  const [html, setHtml] = useState<string>(state.html || '');
  const [websiteId, setWebsiteId] = useState<string | null>(state.websiteId || null);

  const [error, setError] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [showPalette, setShowPalette] = useState<boolean>(false);
  const [openIconPicker, setOpenIconPicker] = useState<{sectionIndex:number; itemIndex:number} | null>(null);
  // History
  const [history, setHistory] = useState<Array<{ sections: V3Section[]; metadata: V3Metadata | null }>>([]);
  const [future, setFuture] = useState<Array<{ sections: V3Section[]; metadata: V3Metadata | null }>>([]);
  // Responsive preview
  const [device, setDevice] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');

  const dragIndexRef = useRef<number | null>(null);

  const canSave = !!html && !saving;
  const canDeploy = !!websiteId && !deploying;
  const canUndo = history.length > 0;
  const canRedo = future.length > 0;

  useEffect(() => {
    document.title = 'Website Editor | Explanatum';
  }, []);

  

  // D: Load by siteId when present
  useEffect(() => {
    const siteId = params['*'] || (params as any)?.siteId; // react-router v6 param name support
    const id = (params as any)?.siteId;
    const actualId = id || undefined;
    if (!actualId) return;
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token || '';
        const data: GetWebsiteResponse = await requestJson(
          `${API_BASE}/api/website/get?websiteId=${encodeURIComponent(actualId)}`,
          { headers: { 'Authorization': `Bearer ${token}` } },
          'Failed to load website'
        );
        setWebsiteId(data.website.id);
        setHtml(data.website.html || '');
        const wdata = data.website.website_data || {} as any;
        if (wdata.metadata) setMetadata(wdata.metadata);
        if (Array.isArray(wdata.sections)) setSections(wdata.sections as V3Section[]);
      } catch (e: any) {
        setError(e.message || 'Failed to load site');
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.siteId]);

  // Drag and drop reorder handlers
  const onDragStart = (idx: number) => (e: React.DragEvent) => {
    dragIndexRef.current = idx;
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onDrop = (idx: number) => async (e: React.DragEvent) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    dragIndexRef.current = null;
    if (from == null || from === idx) return;
    recordHistory();
    setSections(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(idx, 0, moved);
      return next;
    });
    setSelectedIndex(idx);
  };

  // History helpers
  const recordHistory = () => {
    setHistory(prev => [...prev, { sections: sections.map(s=>({ ...s })), metadata: metadata ? { ...metadata } : null }]);
    setFuture([]);
  };
  const undo = () => {
    if (!canUndo) return;
    const prev = history[history.length - 1];
    setHistory(h => h.slice(0, h.length - 1));
    setFuture(f => [...f, { sections: sections.map(s=>({ ...s })), metadata: metadata ? { ...metadata } : null }]);
    setSections(prev.sections);
    setMetadata(prev.metadata);
  };
  const redo = () => {
    if (!canRedo) return;
    const nxt = future[future.length - 1];
    setFuture(f => f.slice(0, f.length - 1));
    setHistory(h => [...h, { sections: sections.map(s=>({ ...s })), metadata: metadata ? { ...metadata } : null }]);
    setSections(nxt.sections);
    setMetadata(nxt.metadata);
  };

  // A: inject modern CSS/theme into assembled HTML
  const applyThemeToHtml = (rawHtml: string, theme: any): string => {
    try {
      const tokens = {
        font: theme?.font || 'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
        primary: theme?.primary || '#2563eb',
        radius: Number(theme?.radius ?? 12),
        spacing: Number(theme?.spacing ?? 1.0),
        shadow: (theme?.shadow || 'md'),
      };
      const shadowMap: Record<string,string> = { sm: '0 1px 2px rgba(0,0,0,.06)', md: '0 4px 12px rgba(0,0,0,.08)', lg: '0 10px 24px rgba(0,0,0,.12)' };
      const css = `:root{--font:${tokens.font};--primary:${tokens.primary};--radius:${tokens.radius}px;--shadow:${shadowMap[tokens.shadow]||shadowMap.md};}
      html,body{font-family:var(--font);color:#111827;background:#fff}
      section{padding:clamp(48px,${tokens.spacing*6}vw,112px)}
      h1{font-size:clamp(32px,4.5vw,56px);line-height:1.05;letter-spacing:-.02em}
      h2{font-size:clamp(24px,3vw,40px);line-height:1.1}
      h3{font-size:clamp(18px,2.2vw,28px);line-height:1.2}
      p{color:#374151;line-height:1.7}
      .btn{display:inline-block;padding:12px 18px;border-radius:var(--radius);background:var(--primary);color:#fff;text-decoration:none;box-shadow:var(--shadow)}
      .card{border-radius:var(--radius);box-shadow:var(--shadow);background:#fff}
      .muted{color:#6B7280}
      .container{max-width:1120px;margin:0 auto;padding:0 20px}
      `;
      if (rawHtml.includes('</head>')) {
        return rawHtml.replace('</head>', `<style>${css}</style></head>`);
      }
      return `<!doctype html><html><head><meta charset='utf-8'><style>${css}</style></head><body>${rawHtml}</body></html>`;
    } catch {
      return rawHtml;
    }
  };

  // Re-assemble HTML when sections change
  useEffect(() => {
    let stopped = false;
    const run = async () => {
      if (!metadata || sections.length === 0) return;
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token || '';
        const assembled: AssembleResponse = await requestJson(
          `${API_BASE}/api/website/generate-v3/assemble`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ metadata, sections, userId: sessionData.session?.user?.id, showLogos: true }),
          },
          'Failed to assemble website'
        );
        if (!stopped && assembled && typeof assembled.html === 'string') {
          const themed = applyThemeToHtml(assembled.html, (metadata as any)?.theme || {});
          setHtml(themed);
        }
      } catch (e: any) {
        setError(e.message || 'Failed to update preview');
      }
    };
    run();
    return () => { stopped = true; };
  }, [metadata, sections]);

  // Autosave when websiteId exists and content changes
  useEffect(() => {
    if (!websiteId) return;
    const timeout = setTimeout(() => {
      if (!saving && html) {
        handleSave();
      }
    }, 2000);
    return () => clearTimeout(timeout);
  }, [websiteId, html, metadata, sections]);

  const handleSave = async () => {
    if (!html) return;
    setSaving(true);
    setError('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || '';
      const websiteData = {
        html,
        website_data: { metadata, sections },
      };
      const data: SaveWebsiteResponse = await requestJson(
        `${API_BASE}/api/website/save`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ websiteData, userId: sessionData.session?.user?.id }),
        },
        'Failed to save website'
      );
      setWebsiteId(data.website.id);
      // D: navigate to persistent editor route after first save
      if (!(params as any)?.siteId) {
        navigate(`/website/editor/${data.website.id}`);
      }
      setStatus('Saved');
      setTimeout(() => setStatus(''), 2000);
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDeploy = async () => {
    if (!websiteId) return;
    setDeploying(true);
    setError('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || '';
      const res: DeployResponse = await requestJson(
        `${API_BASE}/api/website/deploy`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ websiteId, userId: sessionData.session?.user?.id }),
        },
        'Failed to deploy website'
      );
      setStatus('Deployed');
      if (res?.url) window.open(res.url, '_blank');
    } catch (e: any) {
      setError(e.message || 'Failed to deploy');
    } finally {
      setDeploying(false);
    }
  };

  const sectionLabel = (s: V3Section) => s.heading || s.kind || s.id;

  const addSection = (type: 'hero' | 'text' | 'image' | 'pricing' | 'features' | 'testimonials' | 'cta') => {
    recordHistory();
    const base: V3Section = { id: `${Date.now()}_${Math.random().toString(36).slice(2)}`, kind: type };
    if (type === 'hero') Object.assign(base, { heading: 'Welcome', body: 'Your amazing product', align: 'center' });
    if (type === 'text') Object.assign(base, { heading: 'Section title', body: 'Write something great here.' });
    if (type === 'image') Object.assign(base, { heading: 'Image', imageUrl: '', caption: '' });
    if (type === 'pricing') Object.assign(base, { heading: 'Pricing', body: 'Simple transparent pricing.' });
    if (type === 'features') Object.assign(base, { heading: 'Features', items: [
      { title: 'Fast setup', text: 'Get online in minutes.' },
      { title: 'Modern design', text: 'Beautiful on every device.' },
      { title: 'SEO friendly', text: 'Built to perform.' },
    ]});
    if (type === 'testimonials') Object.assign(base, { heading: 'What customers say', quotes: [
      { quote: 'Absolutely transformed our site.', author: 'Alex P.' },
      { quote: 'Looks stunning and converts.', author: 'Rhea L.' },
    ]});
    if (type === 'cta') Object.assign(base, { heading: 'Ready to start?', body: 'Launch your site today.', buttonText: 'Get started', buttonHref: '#' });
    setSections(prev => [...prev, base]);
    setSelectedIndex(sections.length);
  };

  const removeSection = (idx: number) => {
    if (!window.confirm('Remove this section?')) return;
    recordHistory();
    setSections(prev => prev.filter((_, i) => i !== idx));
    setSelectedIndex(null);
  };

  const duplicateSection = (idx: number) => {
    recordHistory();
    setSections(prev => {
      const next = [...prev];
      const copy = { ...next[idx], id: `${Date.now()}_${Math.random().toString(36).slice(2)}` } as V3Section;
      next.splice(idx + 1, 0, copy);
      return next;
    });
    setSelectedIndex(idx + 1);
  };

  const insertSection = (idx: number, type: 'hero' | 'text' | 'image' | 'pricing' | 'features' | 'testimonials' | 'cta', position: 'above' | 'below') => {
    recordHistory();
    const base: V3Section = { id: `${Date.now()}_${Math.random().toString(36).slice(2)}`, kind: type };
    if (type === 'hero') Object.assign(base, { heading: 'Welcome', body: 'Your amazing product', align: 'center' });
    if (type === 'text') Object.assign(base, { heading: 'Section title', body: 'Write something great here.' });
    if (type === 'image') Object.assign(base, { heading: 'Image', imageUrl: '', caption: '' });
    if (type === 'pricing') Object.assign(base, { heading: 'Pricing', body: 'Simple transparent pricing.' });
    if (type === 'features') Object.assign(base, { heading: 'Features', items: [] });
    if (type === 'testimonials') Object.assign(base, { heading: 'What customers say', quotes: [] });
    if (type === 'cta') Object.assign(base, { heading: 'Ready to start?', body: 'Launch your site today.', buttonText: 'Get started', buttonHref: '#' });
    setSections(prev => {
      const next = [...prev];
      next.splice(position === 'above' ? idx : idx + 1, 0, base);
      return next;
    });
    setSelectedIndex(position === 'above' ? idx : idx + 1);
  };

  // C: AI refine actions
  const handleRewriteSection = async (idx: number) => {
    try {
      const sec = sections[idx];
      setStatus('Rewriting with AI…');
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || '';
      const res = await requestJson(
        `${API_BASE}/api/website/ai/rewrite-section`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ section: sec, metadata, userId: sessionData.session?.user?.id }),
        },
        'Failed to rewrite section'
      );
      const newBody = res?.section?.body || res?.text || '';
      if (newBody) {
        recordHistory();
        setSections(prev => prev.map((x, i) => i === idx ? { ...x, body: newBody } : x));
        setStatus('Section updated');
        setTimeout(()=>setStatus(''), 1200);
      }
    } catch (e:any) {
      setError(e.message || 'AI rewrite failed');
    }
  };

  const handleGenerateImage = async (idx: number) => {
    try {
      const sec = sections[idx];
      const prompt = `${sec.heading || 'Website hero'} ${sec.body || ''}`.slice(0, 500);
      setStatus('Generating image…');
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token || '';
      const res = await requestJson(
        `${API_BASE}/api/website/ai/generate-image`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ prompt, userId: sessionData.session?.user?.id }),
        },
        'Failed to generate image'
      );
      const url = res?.imageUrl || res?.url || '';
      if (url) {
        recordHistory();
        setSections(prev => prev.map((x, i) => i === idx ? { ...x, imageUrl: url } : x));
        setStatus('Image added');
        setTimeout(()=>setStatus(''), 1200);
      }
    } catch (e:any) {
      setError(e.message || 'AI image failed');
    }
  };

  if (!metadata || sections.length === 0) {
    return (
      <div className="max-w-5xl mx-auto px-4">
        <div className="bg-white rounded-xl shadow p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Website Editor</h1>
          <p className="text-gray-600 mb-4">No generated site found. Please generate a website first.</p>
          <button onClick={() => navigate('/marketing/ai-website-generator')} className="px-4 py-2 rounded bg-gray-900 text-white">Go to Generator</button>
        </div>

      {/* Theme Controls */}
      <div className="mt-6 bg-white rounded-xl border p-4">
        <h2 className="text-lg font-semibold mb-3">Theme</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Preset</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={(metadata as any)?.theme?.preset || 'modern'}
              onChange={(e)=>{
                const preset = e.target.value;
                recordHistory();
                const presets: Record<string, any> = {
                  modern: { font: 'inter', primary: '#2563eb', radius: 12, shadow: 'md', spacing: 1.0 },
                  minimal: { font: 'system-ui', primary: '#111827', radius: 4, shadow: 'sm', spacing: 0.9 },
                  elegant: { font: 'serif-display', primary: '#7c3aed', radius: 8, shadow: 'md', spacing: 1.1 },
                  bold: { font: 'rounded-sans', primary: '#ef4444', radius: 16, shadow: 'lg', spacing: 1.05 },
                };
                const next = presets[preset] || presets.modern;
                setMetadata(m => ({ ...(m||{}), theme: { ...(m?.theme||{}), preset, ...next } }));
              }}
            >
              <option value="modern">Modern</option>
              <option value="minimal">Minimal</option>
              <option value="elegant">Elegant</option>
              <option value="bold">Bold</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Font</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={(metadata as any)?.theme?.font || 'system-ui'}
              onChange={(e)=>{
                const v = e.target.value;
                recordHistory();
                setMetadata(m => ({ ...(m||{}), theme: { ...(m?.theme||{}), font: v } }));
              }}
            >
              <option value="system-ui">System UI</option>
              <option value="inter">Inter</option>
              <option value="serif-display">Serif Display</option>
              <option value="rounded-sans">Rounded Sans</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
            <input
              type="color"
              className="w-full h-10 border rounded"
              value={(metadata as any)?.theme?.primary || '#2563eb'}
              onChange={(e)=>{
                const v = e.target.value;
                recordHistory();
                setMetadata(m => ({ ...(m||{}), theme: { ...(m?.theme||{}), primary: v } }));
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Corner Radius</label>
            <input type="number" min={0} max={24} className="w-full border rounded px-3 py-2" value={(metadata as any)?.theme?.radius ?? 12} onChange={(e)=>{ const v=Number(e.target.value)||0; recordHistory(); setMetadata(m=> ({ ...(m||{}), theme: { ...(m?.theme||{}), radius: v } })); }} />
          </div>
          <div className="flex items-end">
            <button onClick={()=>setStatus('Theme updated')} className="px-3 py-2 rounded bg-gray-900 text-white">Apply</button>
          </div>
        </div>
      </div>

      {/* Palette Modal */}
      {showPalette && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add a section</h3>
              <button className="text-gray-600" onClick={()=>setShowPalette(false)} aria-label="Close">✕</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button onClick={()=>{addSection('hero'); setShowPalette(false);}} className="border rounded-lg p-4 text-left hover:border-blue-500">
                <div className="font-medium mb-1">Hero</div>
                <div className="text-sm text-gray-600">Large heading and intro content to make a strong first impression.</div>
              </button>
              <button onClick={()=>{addSection('text'); setShowPalette(false);}} className="border rounded-lg p-4 text-left hover:border-blue-500">
                <div className="font-medium mb-1">Text</div>
                <div className="text-sm text-gray-600">Simple content block with title and body.</div>
              </button>
              <button onClick={()=>{addSection('image'); setShowPalette(false);}} className="border rounded-lg p-4 text-left hover:border-blue-500">
                <div className="font-medium mb-1">Image</div>
                <div className="text-sm text-gray-600">Add an image with optional caption.</div>
              </button>
              <button onClick={()=>{addSection('pricing'); setShowPalette(false);}} className="border rounded-lg p-4 text-left hover:border-blue-500">
                <div className="font-medium mb-1">Pricing</div>
                <div className="text-sm text-gray-600">Present your pricing clearly.</div>
              </button>
              <button onClick={()=>{addSection('features'); setShowPalette(false);}} className="border rounded-lg p-4 text-left hover:border-blue-500">
                <div className="font-medium mb-1">Features</div>
                <div className="text-sm text-gray-600">Grid of feature points with titles and text.</div>
              </button>
              <button onClick={()=>{addSection('testimonials'); setShowPalette(false);}} className="border rounded-lg p-4 text-left hover:border-blue-500">
                <div className="font-medium mb-1">Testimonials</div>
                <div className="text-sm text-gray-600">Customer quotes to build trust.</div>
              </button>
              <button onClick={()=>{addSection('cta'); setShowPalette(false);}} className="border rounded-lg p-4 text-left hover:border-blue-500">
                <div className="font-medium mb-1">Call to Action</div>
                <div className="text-sm text-gray-600">Strong CTA band with button.</div>
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Website Editor</h1>
        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-1 mr-2">
            <button onClick={()=>setDevice('mobile')} className={`px-2 py-1 rounded text-xs border ${device==='mobile'?'bg-gray-900 text-white':'bg-white'}`}>Mobile</button>
            <button onClick={()=>setDevice('tablet')} className={`px-2 py-1 rounded text-xs border ${device==='tablet'?'bg-gray-900 text-white':'bg-white'}`}>Tablet</button>
            <button onClick={()=>setDevice('desktop')} className={`px-2 py-1 rounded text-xs border ${device==='desktop'?'bg-gray-900 text-white':'bg-white'}`}>Desktop</button>
          </div>
          <button onClick={undo} disabled={!canUndo} className={`px-3 py-2 rounded ${canUndo ? 'bg-gray-100' : 'bg-gray-200 text-gray-500'}`}>Undo</button>
          <button onClick={redo} disabled={!canRedo} className={`px-3 py-2 rounded ${canRedo ? 'bg-gray-100' : 'bg-gray-200 text-gray-500'}`}>Redo</button>
          <button onClick={handleSave} disabled={!canSave} className={`px-3 py-2 rounded ${canSave ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}>{saving ? 'Saving…' : 'Save'}</button>
          <button onClick={handleDeploy} disabled={!canDeploy} className={`px-3 py-2 rounded ${canDeploy ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'}`}>{deploying ? 'Deploying…' : 'Publish'}</button>
        </div>
      </div>

      {error && (<div className="mb-3 rounded bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</div>)}
      {status && (<div className="mb-3 rounded bg-green-50 border border-green-200 p-3 text-sm text-green-800">{status}</div>)}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Column 1: Sections + Palette */}
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Sections</h2>
            <div className="flex items-center gap-2 text-xs"><button onClick={() => setShowPalette(true)} className="px-2 py-1 rounded bg-gray-900 text-white">+ Add</button></div>
          </div>
          <div className="space-y-2">
            {sections.map((s, idx) => (
              <div
                key={s.id}
                draggable
                onClick={() => setSelectedIndex(idx)}
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver(idx)}
                onDrop={onDrop(idx)}
                className={`rounded border p-3 bg-gray-50 cursor-move ${selectedIndex===idx?'ring-2 ring-blue-500':''}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium truncate mr-2">{sectionLabel(s)}</div>
                  <div className="flex items-center gap-2">
                    <div className="relative group">
                      <button onClick={(e)=>{e.stopPropagation(); duplicateSection(idx);}} className="text-xs px-2 py-0.5 rounded border">Duplicate</button>
                    </div>
                    <div className="relative group">
                      <button className="text-xs px-2 py-0.5 rounded border" onClick={(e)=>{e.stopPropagation(); insertSection(idx, (s.kind as any) || 'text', 'above');}}>Insert Above</button>
                    </div>
                    <div className="relative group">
                      <button className="text-xs px-2 py-0.5 rounded border" onClick={(e)=>{e.stopPropagation(); insertSection(idx, (s.kind as any) || 'text', 'below');}}>Insert Below</button>
                    </div>
                    <button onClick={(e)=>{e.stopPropagation(); removeSection(idx);}} className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">Remove</button>
                  </div>
                </div>
                <textarea
                  className="mt-2 w-full border rounded p-2 text-sm"
                  rows={3}
                  value={s.body || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    recordHistory();
                    setSections(prev => prev.map((x, i) => i === idx ? { ...x, body: v } : x));
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Column 2: Inspector */}
        <div className="bg-white rounded-xl border p-4">
          <h2 className="text-lg font-semibold mb-3">Inspector</h2>
          {selectedIndex==null ? (
            <div className="text-sm text-gray-600">Select a section to edit its properties.</div>
          ) : (
            <div className="space-y-3">
              {/* Variant control per kind */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Layout Variant</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={(sections[selectedIndex] as any)?.variant || ''}
                  onChange={(e)=>{ const v=e.target.value; recordHistory(); setSections(prev=>prev.map((x,i)=> i===selectedIndex ? { ...x, variant: v || undefined } : x)); }}
                >
                  <option value="">Default</option>
                  {sections[selectedIndex]?.kind === 'hero' && (<>
                    <option value="split-left">Split (Image Left)</option>
                    <option value="">Split (Image Right)</option>
                    <option value="cover">Cover</option>
                  </>)}
                  {sections[selectedIndex]?.kind === 'features' && (<>
                    <option value="icons-grid">Icons Grid</option>
                    <option value="cards">Cards</option>
                  </>)}
                  {sections[selectedIndex]?.kind === 'testimonials' && (<>
                    <option value="cards">Cards</option>
                    <option value="carousel">Carousel</option>
                  </>)}
                  {sections[selectedIndex]?.kind === 'pricing' && (<>
                    <option value="tiers">Tiers</option>
                    <option value="cards">Cards</option>
                  </>)}
                </select>
              </div>
              {sections[selectedIndex]?.kind === 'hero' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hero Split Side</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className={`px-3 py-1.5 border rounded text-sm ${((sections[selectedIndex] as any)?.variant)==='split-left' ? 'bg-blue-50 border-blue-300' : ''}`}
                      onClick={()=>{ recordHistory(); setSections(prev=>prev.map((x,i)=> i===selectedIndex ? { ...x, variant: 'split-left' } : x)); }}
                    >Image Left</button>
                    <button
                      type="button"
                      className={`px-3 py-1.5 border rounded text-sm ${!((sections[selectedIndex] as any)?.variant) ? 'bg-blue-50 border-blue-300' : ''}`}
                      onClick={()=>{ recordHistory(); setSections(prev=>prev.map((x,i)=> i===selectedIndex ? { ...x, variant: undefined } : x)); }}
                    >Image Right</button>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Use "Cover" in the dropdown above for a full-width background hero.</div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <div className="text-xs inline-block px-2 py-1 rounded bg-gray-100 border">{sections[selectedIndex]?.kind || 'custom'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Heading</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={sections[selectedIndex]?.heading || ''}
                  onChange={(e)=>{
                    const v = e.target.value;
                    recordHistory();
                    setSections(prev=>prev.map((x,i)=> i===selectedIndex ? { ...x, heading: v } : x));
                  }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Align</label>
                  <select
                    className="w-full border rounded px-3 py-2"
                    value={(sections[selectedIndex] as any)?.align || 'left'}
                    onChange={(e)=>{
                      const v = e.target.value;
                      recordHistory();
                      setSections(prev=>prev.map((x,i)=> i===selectedIndex ? { ...x, align: v } : x));
                    }}
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Background</label>
                  <input
                    type="color"
                    className="w-full h-10 border rounded"
                    value={(sections[selectedIndex] as any)?.bgColor || '#ffffff'}
                    onChange={(e)=>{
                      const v = e.target.value;
                      recordHistory();
                      setSections(prev=>prev.map((x,i)=> i===selectedIndex ? { ...x, bgColor: v } : x));
                    }}
                  />
                </div>
              </div>
              {sections[selectedIndex]?.kind === 'image' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                    <input
                      className="w-full border rounded px-3 py-2"
                      placeholder="https://..."
                      value={(sections[selectedIndex] as any)?.imageUrl || ''}
                      onChange={(e)=>{
                        const v = e.target.value;
                        recordHistory();
                        setSections(prev=>prev.map((x,i)=> i===selectedIndex ? { ...x, imageUrl: v } : x));
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Caption</label>
                    <input
                      className="w-full border rounded px-3 py-2"
                      value={(sections[selectedIndex] as any)?.caption || ''}
                      onChange={(e)=>{
                        const v = e.target.value;
                        recordHistory();
                        setSections(prev=>prev.map((x,i)=> i===selectedIndex ? { ...x, caption: v } : x));
                      }}
                    />
                  </div>
                </div>
              )}
              {sections[selectedIndex]?.kind === 'features' && (
                <div className="space-y-2">
                  <div className="text-sm text-gray-600">Feature items</div>
                  {(((sections[selectedIndex] as any)?.items)||[]).map((it:any, i:number)=> (
                    <div key={i} className="grid grid-cols-3 gap-2 relative">
                      <input className="border rounded px-2 py-1" placeholder="Title" value={it.title||''} onChange={(e)=>{ const v=e.target.value; recordHistory(); setSections(prev=>prev.map((x,idx)=> idx===selectedIndex ? { ...x, items: (x as any).items.map((y:any, yi:number)=> yi===i? { ...y, title: v}: y) } : x)); }} />
                      <input className="border rounded px-2 py-1" placeholder="Text" value={it.text||''} onChange={(e)=>{ const v=e.target.value; recordHistory(); setSections(prev=>prev.map((x,idx)=> idx===selectedIndex ? { ...x, items: (x as any).items.map((y:any, yi:number)=> yi===i? { ...y, text: v}: y) } : x)); }} />
                      <div className="flex items-center gap-2">
                        <input className="border rounded px-2 py-1 w-full" placeholder="Icon (e.g., star, check, bolt, globe, lock, or emoji)" value={it.icon||''} onChange={(e)=>{ const v=e.target.value; recordHistory(); setSections(prev=>prev.map((x,idx)=> idx===selectedIndex ? { ...x, items: (x as any).items.map((y:any, yi:number)=> yi===i? { ...y, icon: v}: y) } : x)); }} />
                        <button type="button" className="px-2 py-1 border rounded text-xs" onClick={()=> setOpenIconPicker({ sectionIndex: selectedIndex, itemIndex: i })}>Pick</button>
                        {openIconPicker && openIconPicker.sectionIndex===selectedIndex && openIconPicker.itemIndex===i && (
                          <div className="absolute right-0 top-full mt-1 z-10 bg-white border rounded shadow p-3 grid grid-cols-6 gap-2 w-[360px]">
                            {['star','check','bolt','globe','lock','💡','🚀','🔒'].map(opt=> (
                              <button key={opt} className="flex items-center justify-center h-9 border rounded text-base hover:bg-gray-50" title={opt}
                                onClick={()=>{ recordHistory(); setSections(prev=>prev.map((x,idx)=> idx===selectedIndex ? { ...x, items: (x as any).items.map((y:any, yi:number)=> yi===i? { ...y, icon: opt}: y) } : x)); setOpenIconPicker(null); }}>{opt}</button>
                            ))}
                            <button className="col-span-3 px-2 py-1 border rounded text-xs" onClick={()=>{ recordHistory(); setSections(prev=>prev.map((x,idx)=> idx===selectedIndex ? { ...x, items: (x as any).items.map((y:any, yi:number)=> yi===i? ({ ...y, icon: undefined }): y) } : x)); setOpenIconPicker(null); }}>Clear</button>
                            <button className="col-span-3 text-xs text-gray-600 underline" onClick={()=> setOpenIconPicker(null)}>Close</button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <button className="mt-1 text-xs px-2 py-1 rounded border" onClick={()=>{ recordHistory(); setSections(prev=>prev.map((x,idx)=> idx===selectedIndex ? { ...x, items: ([...((x as any).items||[]), { title: 'New feature', text: 'Description' }]) } : x)); }}>+ Add feature</button>
                </div>
              )}
              {sections[selectedIndex]?.kind === 'testimonials' && (
                <div className="space-y-2">
                  <div className="text-sm text-gray-600">Testimonials</div>
                  {(((sections[selectedIndex] as any)?.quotes)||[]).map((q:any, i:number)=> (
                    <div key={i} className="grid grid-cols-2 gap-2">
                      <input className="border rounded px-2 py-1" placeholder="Quote" value={q.quote||''} onChange={(e)=>{ const v=e.target.value; recordHistory(); setSections(prev=>prev.map((x,idx)=> idx===selectedIndex ? { ...x, quotes: (x as any).quotes.map((y:any, yi:number)=> yi===i? { ...y, quote: v}: y) } : x)); }} />
                      <input className="border rounded px-2 py-1" placeholder="Author" value={q.author||''} onChange={(e)=>{ const v=e.target.value; recordHistory(); setSections(prev=>prev.map((x,idx)=> idx===selectedIndex ? { ...x, quotes: (x as any).quotes.map((y:any, yi:number)=> yi===i? { ...y, author: v}: y) } : x)); }} />
                    </div>
                  ))}
                  <button className="mt-1 text-xs px-2 py-1 rounded border" onClick={()=>{ recordHistory(); setSections(prev=>prev.map((x,idx)=> idx===selectedIndex ? { ...x, quotes: ([...((x as any).quotes||[]), { quote: 'Amazing!', author: 'Happy customer' }]) } : x)); }}>+ Add quote</button>
                </div>
              )}
              {sections[selectedIndex]?.kind === 'cta' && (
                <div className="space-y-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                    <input className="w-full border rounded px-3 py-2" value={(sections[selectedIndex] as any)?.buttonText || ''} onChange={(e)=>{ const v=e.target.value; recordHistory(); setSections(prev=>prev.map((x,i)=> i===selectedIndex ? { ...x, buttonText: v } : x)); }} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
                    <input className="w-full border rounded px-3 py-2" value={(sections[selectedIndex] as any)?.buttonHref || ''} onChange={(e)=>{ const v=e.target.value; recordHistory(); setSections(prev=>prev.map((x,i)=> i===selectedIndex ? { ...x, buttonHref: v } : x)); }} />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Column 3: Preview */}
        <div className="bg-white rounded-xl border p-4 lg:col-span-1">
          <h2 className="text-lg font-semibold mb-3">Preview</h2>
          <div className="border rounded overflow-hidden h-[700px] flex items-start justify-center bg-gray-100">
            <div className={`${device==='mobile'?'w-[390px]':'w-full'} ${device==='tablet'?'max-w-[820px] w-full':''} ${device==='desktop'?'w-full':''} bg-white h-full`}> 
              <iframe title="Preview" className="w-full h-full" sandbox="allow-scripts" srcDoc={html || '<!doctype html><html><head><meta charset=\'utf-8\'><title>Preview unavailable</title></head><body style=\'font-family:ui-sans-serif,system-ui,sans-serif;padding:16px;color:#374151\'>No preview available.</body></html>'} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Editor;
